import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';
import {
    ACard,
    Period,
    PeriodSelector,
    Skeleton,
    AChartX
} from "@atiautomacao/ati-ui-library";
import {GeneratedEnergy} from "../DashboardPowerPlantCharts";
import {RootState} from "../../../../Config/Store";
import IconButton from "@mui/material/IconButton";
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {downloadFile} from "../../../../Utils/DownloadUtils";
import {
    getGeneratedEnergyDay,
    getGeneratedEnergyMonth,
    getGeneratedEnergyYear
} from "./DashboardPowerPlantChartsReducer";
import {Box, Divider, Stack, Typography, useTheme} from "@mui/material";
import {format} from "date-fns";
import {ptBR} from "date-fns/locale";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {isArray} from "lodash";
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../../Config/Constants";
import { getChartXOptions } from '../../../../Utils/ChartXOption';
import { SimCardDownloadOutlined } from '@mui/icons-material';
import { LocalDateTimeFormatISO } from "../../../../Utils/DateFormatPatternUtils";
import {useSnackbar} from "notistack";
import {CSV_EXPORT_ERROR_MESSAGE, CSV_EXPORT_NO_DATA_MESSAGE} from "../../../../Utils/CsvExportMessages";
import {
    buildAxisTooltipFormatter,
    buildOverlappedBarSeries,
    ChartValue
} from "../../../../Utils/OverlappedBarSeriesUtils";

export function resolveGenerationEnergyCsvPath(groupBy: string, powerPlantId: number | string): string {
    switch (groupBy) {
        case "year":
            return `api/generation-energy/power-station/${powerPlantId}/csv/month`;
        case "general":
            return `api/generation-energy/power-station/${powerPlantId}/csv/year`;
        default:
            return `api/generation-energy/power-station/${powerPlantId}/csv/day`;
    }
}

export function resolveIrradianceUnit(groupBy: string): "Wh/m²" | "kWh/m²" {
    return groupBy === "year" || groupBy === "general" ? "kWh/m²" : "Wh/m²";
}

const ENERGY_UNIT = "MWh";
const IRRADIANCE_SERIES_NAME = "Irradiação";
const EXPECTED_IRRADIANCE_SERIES_NAME = "Irradiação Esperada";
// Linhas acima das barras sobrepostas (camadas usam z 2..5).
const LINE_Z = 10;
const IRRADIANCE_COLOR = "#B7862C";
const EXPECTED_IRRADIANCE_COLOR = "#C9B65A";

// Ordem e cores conforme Figma (SGD-3827); define também a ordem na legenda e no tooltip.
const ENERGY_SERIES: Array<{ name: string; color: string; getValue: (generation: GeneratedEnergy) => number | undefined }> = [
    {name: "Energia Esp. Corrigida", color: "#C5D8EE", getValue: (generation) => generation.expectedEnergyCorrectedByIrradiation},
    {name: "Energia Esperada", color: "#4A6FA5", getValue: (generation) => generation.expectedInjectedEnergy},
    {name: "Energia Gerada", color: "#2D5DE7", getValue: (generation) => generation.generatedEnergy},
    {name: "Energia Injetada", color: "#1BB483", getValue: (generation) => generation.injectedEnergy},
];

// Mantém o comportamento atual: ausência (e zero) vira '-'.
const toChartValue = (value: number | undefined | null): ChartValue => value ? truncateNumber(value, 2) : "-";

const GenerationChart: FunctionComponent<{ powerPlant: any;}> = ({powerPlant}) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.VIEW_ENERGY_GENERATION_EXPORT, AUTHORITIES.CSV_ENERGY_GENERATION_EXPORT
    ]);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const {generatedEnergy, loading} = useAppSelector((state: RootState) => state.dashboardPowerPlantCharts);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const {enqueueSnackbar} = useSnackbar();

    const initialPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const [period, setPeriod] = useState<Period>(initialPeriod);
    const irradianceUnit = resolveIrradianceUnit(period.groupBy);

    const generateSeries = useCallback(() => {
        // As 4 energias numa mesma barra por período, maior ao fundo e menor à frente. SGD-3827.
        const energyBars = buildOverlappedBarSeries(ENERGY_SERIES.map((energy) => ({
            name: energy.name,
            color: energy.color,
            values: generatedEnergy.map((generation: GeneratedEnergy) => toChartValue(energy.getValue(generation))),
        })));

        const irradiance = {
            name: IRRADIANCE_SERIES_NAME,
            type: 'line',
            smooth: true,
            symbol: 'emptyCircle',
            z: LINE_Z,
            itemStyle: {color: IRRADIANCE_COLOR},
            lineStyle: {color: IRRADIANCE_COLOR, width: 2},
            tooltip: {
                valueFormatter: (value: number) => `${value} ${irradianceUnit}`
            },
            yAxisIndex: 1,
            data: generatedEnergy.map((generation: GeneratedEnergy) => toChartValue(generation.irradiance))
        };

        const expectedIrradiance = {
            name: EXPECTED_IRRADIANCE_SERIES_NAME,
            type: 'line',
            smooth: true,
            symbol: 'emptyCircle',
            z: LINE_Z,
            itemStyle: {color: EXPECTED_IRRADIANCE_COLOR},
            lineStyle: {color: EXPECTED_IRRADIANCE_COLOR, width: 2, type: 'dashed'},
            tooltip: {
                valueFormatter: (value: number) => `${value} ${irradianceUnit}`
            },
            yAxisIndex: 1,
            data: generatedEnergy.map((generation: GeneratedEnergy) => toChartValue(generation.expectedIrradiance))
        };

        return [...energyBars, expectedIrradiance, irradiance];
    }, [generatedEnergy, irradianceUnit]);

    const getMaxYAxisValue = useCallback((): number => {
        // Considera as 4 energias para nenhuma barra ser cortada (RN 1c). SGD-3827.
        const values = generatedEnergy
            .flatMap((generation: GeneratedEnergy) => ENERGY_SERIES.map((energy) => energy.getValue(generation)))
            .filter((value): value is number => typeof value === "number")
            .map((value) => truncateNumber(value));
        return values.length > 0 ? Math.max(...values) : 0;
    }, [generatedEnergy]);

    const tooltipFormatter = useMemo(() => buildAxisTooltipFormatter([
        ...ENERGY_SERIES.map((energy) => ({name: energy.name, unit: ENERGY_UNIT})),
        {name: EXPECTED_IRRADIANCE_SERIES_NAME, unit: irradianceUnit},
        {name: IRRADIANCE_SERIES_NAME, unit: irradianceUnit},
    ]), [irradianceUnit]);

    const handleCsvDownload = () => {
        const apiUrl = resolveGenerationEnergyCsvPath(period.groupBy, powerPlant.id);
        const params = {
            startDateTime: period.fromDateTime ? LocalDateTimeFormatISO(period.fromDateTime) : null,
            endDateTime: period.toDateTime ? LocalDateTimeFormatISO(period.toDateTime) : null,
        };

        downloadFile(
            apiUrl,
            params,
            "energia-gerada.csv",
            undefined,
            () => enqueueSnackbar(CSV_EXPORT_ERROR_MESSAGE, {variant: "error"}),
            () => enqueueSnackbar(CSV_EXPORT_NO_DATA_MESSAGE, {variant: "info"})
        );
    }

    const handleDateTimeFormat = ():Array<any> => {
        try {
            return generatedEnergy.map((generation) => {
                if (!generation.dateTime) {
                    return "Data Ausente";
                }

                if (period.groupBy === "year" && RegExp(/^\d{4}-\d{2}$/).exec(generation.dateTime)) {
                    const [year, month] = generation.dateTime.split('-');
                    const dataCompleta = new Date(parseInt(year), parseInt(month) - 1, 1);
                    return format(dataCompleta, 'MMM', { locale: ptBR });
                } else if (period.groupBy === "week" || period.groupBy === "month" || period.groupBy === "none") {
                    return format(new Date(generation.dateTime + ' '), 'dd/MM', { locale: ptBR });
                } else if(period.groupBy === "general"){
                    return generation.dateTime;
                }
                else {
                    return "Formato de Data Inválido";
                }
            });
        } catch (error) {
            console.error("Erro ao formatar datas:", error);
            return [];
        }
    }

    const fetchGeneratedEnergy = () => {
        if (powerPlant && checkDiffDays(period.toDateTime, period.fromDateTime) > 0) {
            switch (period.groupBy) {
                case "year":
                    dispatch(getGeneratedEnergyMonth({
                        id: powerPlant.id,
                        startDate: period.fromDateTime,
                        endDate: period.toDateTime
                    }));
                    break;
                case "general":
                    dispatch(getGeneratedEnergyYear({
                        id: powerPlant.id,
                        startDate: period.fromDateTime,
                        endDate: period.toDateTime
                    }));
                    break;
                default:
                    dispatch(getGeneratedEnergyDay({
                        id: powerPlant.id,
                        startDate: period.fromDateTime,
                        endDate: period.toDateTime
                    }));
            }
        }
    };

    useEffect(() => {
        fetchGeneratedEnergy();
    }, [period, powerPlant, dispatch]);

    useInterval(() => {
        fetchGeneratedEnergy();
    }, userConfig.mainUpdateInterval) // 1 minute



    const chartXOptions = getChartXOptions({name:"energia-gerada",date:new Date(new Date())});
    const series = generateSeries();

    // @ts-ignore
    const option: AChartXProps['option'] = {
        ...chartXOptions,
        tooltip: {
            ...chartXOptions.tooltip,
            formatter: tooltipFormatter,
        },
        xAxis: {
            type: 'category',
            data: handleDateTimeFormat(),
            nameLocation: 'middle',
            nameGap: 70,
        },
        yAxis: [
            {
                name: 'MWh',
                max: truncateNumber(getMaxYAxisValue() + getMaxYAxisValue() * 0.15),
                min: 0,
                interval: truncateNumber(getMaxYAxisValue() / 5),
                nameGap: 70,
                namePosition: 'middle'
            },
            {
                name: irradianceUnit,
                max: truncateNumber(Math.max(
                    ...generatedEnergy.map(value => value.irradiance),
                    ...generatedEnergy.map(value => value.expectedIrradiance)
                )),
                min: 0,
                interval: truncateNumber(Math.max(
                    ...generatedEnergy.map(value => value.irradiance),
                    ...generatedEnergy.map(value => value.expectedIrradiance)
                ) / 5),
                nameGap: 70,
                namePosition: 'middle'
            }
        ],
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal', // Altera para horizontal
            bottom: 1, // Coloca a legenda na parte inferior
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            // As camadas de uma energia repetem o name; a legenda mostra um item por série.
            data: Array.from(new Set(series.map(el => el.name)))
        },
        series,
    }

    let content;
    if (loading) {
        content = <Skeleton animation="wave" height={500} variant="rounded" width="100%" />;
    } else if (isArray(generatedEnergy) && generatedEnergy.length > 0) {
        content = (
            <AChartX
                key={`chart-power-energy-${openSubMenu}`}
                option={option}
                width={'100%'}
                height={470}
                loading={false}
                theme={theme.palette.mode}
            />
        );
    } else {
        content = <DataNotFound boxStyle={{ height: 500, width: '100%' }} />;
    }

    return (
        <ACard
            key={`chart-power-energy`}
            styleProps={{
                cardStyle: { height: 595, },
                contentStyle: { padding: '0 16px', height: 570, justifyContent: 'left'},
                titleStyle: { alignItems: 'center', display: 'flex' },
                subTitleStyle: { display: 'none' },
                headerStyle: { flexDirection: 'row', paddingRight:0 },
                headerActionsStyle: { alignItems: 'center', display: 'flex' },
            }}
            title="Energia Gerada"
            ariaLabel={"dashboard-station-generation-chart"}
            headerControlsPosition={"header"}
            headerControls={
                <PeriodSelector
                    styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                    mode='hybrid'
                    disableHourSelector={true}
                    inputFormat={'dd/MM/yyyy'}
                    period={period}
                    periodInterval={{
                        week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6},
                        month: { startTime: "00:00:00" , endTime: "23:59:59", startInterval: 30},
                        year: { startInterval: 11 },
                        general: { startInterval: 6 }
                    }}
                    groupByOptions={['week', 'month', 'year', 'general']}
                    onChange={setPeriod}
                    disabled={loading}
                />
            }
            headerActions={
                    <Stack direction="column" spacing={"8px"}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                            <IconButton disabled={!isAuthorizedToSearchByCsv} size="small" sx={{borderRadius: '4px'}} onClick={handleCsvDownload}>
                                <SimCardDownloadOutlined sx={{ marginRight: '12px' }} />
                                <Typography variant="body2">Baixar como CSV</Typography>

                            </IconButton>
                        </Box>
                        <Divider />
                    </Stack>
            }
        >
            {content}
        </ACard>
    );
}

export default GenerationChart;